// web/js/player-columns/playbackSyncController.ts
//
// Pure drift-correction controller for the multi-column playback sync code
// in `html/js/player_utils.js`. Given a follower's current drift from the
// source player, the source's playback rate, and the follower's current
// playback rate, this function decides what action the caller should take:
//
//   - `none`     — follower is close enough; do nothing.
//   - `setRate`  — smooth, inaudible catch-up via a small playbackRate nudge.
//                  Rate is scaled to the source rate, so 2x playback is
//                  handled naturally (a 4% bump on source rate 2.0 becomes
//                  target rate 2.08).
//   - `snap`     — drift is too large to eat with rate alone within a
//                  reasonable window; the caller should fall back to the
//                  existing seek-based correction (which is rate-limited
//                  separately via a cooldown and pre-compensation).
//
// The function has NO dependency on the DOM or media elements — the caller
// reads the current drift/rates from wherever (sync subscriber callback,
// test), feeds them in, and acts on the returned action. This makes the
// controller trivially unit-testable and lets us iterate on tuning without
// touching the integration.
//
// ### Control parameters
//
// - `ENTER_THRESHOLD_S = 0.050` (50 ms) — the drift must exceed this for
//   the controller to START correcting from an idle state. Lowered from
//   80 ms because the caller now feeds EMA-smoothed drift (alpha 0.15,
//   ~1.75 s settling time at 4 Hz timeupdate), which filters out the
//   tick-to-tick measurement noise that previously required the higher
//   threshold. The ~31 ms steady-state phase offset from play() timing
//   still sits below 50 ms so the controller won't oscillate on it.
//
// - `EXIT_THRESHOLD_S = 0.008` (8 ms) — once the controller is actively
//   correcting, it continues until drift drops BELOW this. The gap between
//   enter and exit is a Schmitt-trigger hysteresis: it prevents the
//   controller from handing back control as soon as drift dips below the
//   enter threshold, which would leave residual drift sitting in the
//   [exit, enter] band and trigger another spike on the next perturbation.
//   Eating drift all the way down to ~zero before releasing means each
//   correction event is ONE ramp, not a sawtooth.
//
// - `SNAP_THRESHOLD_S = 0.350` (350 ms) — above this drift, rate correction
//   would need to run for too long or at too visible a rate. Fall back to
//   snap (seek). Explicit source-initiated seeks and file switches are
//   guaranteed to land here, preserving the snap behavior for those cases.
//
// - `CORRECTION_WINDOW_S = 1.5` (1.5 s) — we want the drift to be eaten
//   within this many seconds of wallclock time. A 120 ms drift eaten over
//   1.5 s requires an 8% rate adjustment: 120 ms / 1500 ms = 0.08. Smaller
//   windows react faster but use more aggressive rate changes; larger
//   windows are gentler but take longer to converge.
//
// - `MAX_RATE_ADJUST = 0.08` (±8%) — hard clamp on rate deviation. Above
//   about 10% the rate shift becomes audible even with `preservesPitch`
//   enabled; 8% is a comfortable ceiling that still eats 120 ms of drift
//   per CORRECTION_WINDOW_S second.
//
// - `RATE_EPSILON = 0.002` — hysteresis. If the computed target rate is
//   within 0.002 of the follower's current rate, we don't emit a new
//   setRate action. This suppresses per-tick rate churn when the controller
//   is already at the right target. It also serves as the "am I currently
//   in correction mode?" detector: if the follower's rate is within epsilon
//   of the source's rate, we are idle; otherwise we are correcting.
//
// ### Algorithm
//
// 1. If `|drift| >= SNAP_THRESHOLD_S` and the caller can snap, return snap.
//    (If the caller cannot snap — e.g. it is in a post-seek cooldown —
//    fall through to rate control. Rate control can't fully eat a huge
//    drift in the cooldown window, but it contains the growth.)
// 2. Detect whether the controller is currently correcting by comparing
//    `currentFollowerRate` to `sourceRate`. If they differ by more than
//    RATE_EPSILON, we are already correcting.
// 3. Decide whether to keep correcting using Schmitt-trigger hysteresis:
//    - If already correcting: continue if `|drift| > EXIT_THRESHOLD_S`.
//    - If not correcting: start only if `|drift| >= ENTER_THRESHOLD_S`.
// 4. If we should NOT correct:
//    - If already correcting (rate is off base), return setRate(sourceRate)
//      to release correction cleanly.
//    - Otherwise return none.
// 5. Compute `adjust = clamp(drift / CORRECTION_WINDOW_S, ±MAX_RATE_ADJUST)`
//    and `targetRate = sourceRate * (1 + adjust)`. If the current follower
//    rate is already approximately at targetRate (within epsilon), return
//    none; else return setRate(targetRate).

export interface SyncCorrectionInput {
  /**
   * Positive drift means the follower is BEHIND the source (needs to speed
   * up). Negative drift means the follower is AHEAD (needs to slow down).
   * Unit: seconds.
   */
  drift: number;
  /**
   * The source player's current playbackRate. Used as the base rate; any
   * correction is applied multiplicatively on top of this so the user's
   * chosen playback speed (1.0, 2.0, 0.5…) is preserved.
   */
  sourceRate: number;
  /**
   * The follower's current playbackRate. Used for hysteresis — if the
   * computed target is very close to this, we return `none` to avoid a
   * setPlaybackRate call on every tick.
   */
  currentFollowerRate: number;
  /**
   * True if the caller is allowed to snap (seek) right now. False during a
   * post-seek cooldown. When false, a drift above `SNAP_THRESHOLD_S` is
   * handled with rate control instead of a snap.
   */
  canSnap: boolean;
}

export type SyncCorrectionAction =
  | { kind: 'none' }
  | { kind: 'setRate'; rate: number }
  | { kind: 'snap' };

// Internal control parameters. Not exported because callers shouldn't need
// to tune them — changing them requires re-verification against the drift
// traces captured during sync debugging.
const ENTER_THRESHOLD_S = 0.035;
const EXIT_THRESHOLD_S = 0.008;
const SNAP_THRESHOLD_S = 0.350;
const CORRECTION_WINDOW_S = 1.5;
const MAX_RATE_ADJUST = 0.08;
const RATE_EPSILON = 0.002;

export function computeSyncCorrection(input: SyncCorrectionInput): SyncCorrectionAction {
  const { drift, sourceRate, currentFollowerRate, canSnap } = input;
  const absDrift = Math.abs(drift);

  // Fast path: too far to eat with rate control — hand off to the snap
  // (seek) path. If snap is temporarily disabled (cooldown), fall through
  // so rate control at least contains the growth.
  if (absDrift >= SNAP_THRESHOLD_S && canSnap) {
    return { kind: 'snap' };
  }

  // Detect whether we are already correcting by looking at the follower's
  // rate. If it differs meaningfully from the source rate, the previous
  // tick left us in an adjusted state and we should continue until drift
  // falls below the exit threshold (Schmitt-trigger hysteresis).
  const isCurrentlyCorrecting =
    Math.abs(currentFollowerRate - sourceRate) > RATE_EPSILON;

  const shouldCorrect = isCurrentlyCorrecting
    ? absDrift > EXIT_THRESHOLD_S
    : absDrift >= ENTER_THRESHOLD_S;

  if (!shouldCorrect) {
    // Either we're idle and drift is below the enter threshold, or we were
    // correcting and drift has now dropped below the exit threshold. In the
    // latter case, release the correction by resetting to source rate.
    if (isCurrentlyCorrecting) {
      return { kind: 'setRate', rate: sourceRate };
    }
    return { kind: 'none' };
  }

  // Proportional rate adjustment, clamped to keep pitch inaudible.
  const rawAdjust = drift / CORRECTION_WINDOW_S;
  const adjust = Math.max(-MAX_RATE_ADJUST, Math.min(MAX_RATE_ADJUST, rawAdjust));
  const targetRate = sourceRate * (1 + adjust);

  if (Math.abs(currentFollowerRate - targetRate) < RATE_EPSILON) {
    return { kind: 'none' };
  }
  return { kind: 'setRate', rate: targetRate };
}

// Window-attach for consumption from non-module scripts such as
// `html/js/player_utils.js`, which runs as a plain global-scoped script.
declare global {
  interface Window {
    computeSyncCorrection?: typeof computeSyncCorrection;
  }
}

if (typeof window !== 'undefined') {
  window.computeSyncCorrection = computeSyncCorrection;
}
